import type { SnapshotMetrics } from '@discovr/types';
import { Skeleton } from '../../../components/ui/skeleton';
import { formatCurrencyINR } from '../../../lib/format';
import { estimateDisplayState, rowMetrics } from './creatorMetricsStatus';
import { useIsRechecking } from './profileRecheckStore';

interface EstimatedCPVCellProps {
  cpv: number | null;
  metrics: SnapshotMetrics | undefined;
  /** Epoch ms. Required, not defaulted: the stall boundary is time-dependent,
   *  and a per-render clock hidden in a default would defeat the caller's. */
  now: number;
  /** Row identity for the metrics-recheck store subscription — see AvgViewsCell. */
  campaignId?: string;
  entryId?: string;
}

/** Est. CPV: a real positive figure always wins (even mid-refetch); otherwise
 *  a skeleton while loading, else "—" — matches formatCpv's never-₹0.00 rule. */
export function EstimatedCPVCell({ cpv, metrics, now, campaignId, entryId }: EstimatedCPVCellProps) {
  const settling = useIsRechecking(campaignId ?? '', entryId ?? '', 'metrics', rowMetrics(metrics));
  if (cpv != null && cpv > 0) {
    return <span className="font-medium text-foreground">{formatCurrencyINR(cpv)}</span>;
  }
  if (estimateDisplayState(metrics, now, settling) === 'loading') {
    // Decorative: the pulse already reads as "working", and with N pending rows
    // a labelled status would queue N announcements for a node that is replaced
    // rather than updated, so it never announces the change it stands for.
    return <Skeleton aria-hidden="true" className="inline-block h-3 w-10" />;
  }
  return <span className="text-muted-foreground">—</span>;
}
